<template>
  <v-card class="mt-4">
    <v-snackbar v-model="snackbar" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
    <v-card-text>
      <v-row class="mb-4">
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedTenant"
            :items="tenants"
            item-text="tenantName"
            item-value="tenantId"
            label="Select Tenant"
            outlined
            dense
            clearable
            @change="onTenantChange"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedBuilding"
            :items="buildings"
            item-text="BuildingName"
            item-value="BuildingId"
            label="Select Building"
            outlined
            dense
            clearable
            :disabled="!selectedTenant"
            @change="onBuildingChange"
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-btn color="secondary" :disabled="!selectedTenant || !selectedBuilding" @click="syncServiceList">Sync serviceList</v-btn>
        </v-col>
        <v-col v-if="selectedTenant && selectedBuilding" cols="12" class="d-flex align-center flex-wrap" style="gap: 8px">
          <span class="text-caption grey--text">Managing:</span>
          <v-chip color="primary" x-small>{{ selectedTenantName }}</v-chip>
          <v-icon x-small>mdi-chevron-right</v-icon>
          <v-chip color="secondary" x-small>{{ selectedBuildingName }}</v-chip>
        </v-col>
      </v-row>
      <template v-if="selectedTenant && selectedBuilding">
        <v-row>
          <v-col cols="12" md="5">
            <h3 class="mb-2">Enabled Services</h3>
            <v-data-table :loading="loading" :headers="enabledHeaders" :items="enabledServices" dense>
              <template v-slot:item.actions="{ item }">
                <v-btn x-small color="error" text @click="removeService(item)">Remove</v-btn>
              </template>
            </v-data-table>
          </v-col>
          <v-col cols="12" md="5">
            <h3 class="mb-2">Add Service from Master List</h3>
            <v-select
              v-model="selectedServiceToAdd"
              :items="availableServices"
              item-text="serviceName"
              item-value="serviceId"
              label="Select service"
              outlined
              dense
              clearable
            />
            <v-btn class="mt-2" color="primary" :disabled="!selectedServiceToAdd" @click="addService">Add Service</v-btn>
          </v-col>
        </v-row>
      </template>
      <v-alert v-else type="info" class="mt-4">Select a tenant and building to manage services</v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
import { getAdminServices, postAdminService, deleteAdminService, getAdminServiceList, postAdminSyncServiceList } from '@/api/'

export default {
  name: 'AdminServiceTable',
  props: {
    tenants: { type: Array, default: () => [] },
    buildings: { type: Array, default: () => [] }
  },
  data() {
    return {
      loading: false,
      snackbar: false,
      snackbarMessage: '',
      selectedTenant: null,
      selectedBuilding: null,
      enabledServices: [],
      serviceList: [],
      selectedServiceToAdd: null,
      enabledHeaders: [
        { text: 'Service ID', value: 'ServiceId' },
        { text: 'Service Name', value: 'serviceName' },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    selectedTenantName() {
      if (!this.selectedTenant) return ''
      const t = this.tenants.find((x) => x.tenantId === this.selectedTenant)
      return t ? (t.tenantName || t.tenantId) : ''
    },
    selectedBuildingName() {
      if (!this.selectedBuilding) return ''
      const b = this.buildings.find((x) => x.BuildingId === this.selectedBuilding)
      return b ? (b.BuildingName || b.BuildingId) : ''
    },
    availableServices() {
      const enabledIds = new Set(this.enabledServices.map((s) => s.ServiceId))
      return this.serviceList.filter((s) => !enabledIds.has(s.serviceId))
    }
  },
  watch: {
    buildings(val) {
      if (!val || !val.length) {
        this.selectedBuilding = null
        this.enabledServices = []
      }
    }
  },
  mounted() {
    this.loadServiceList()
  },
  methods: {
    onTenantChange() {
      this.selectedBuilding = null
      this.enabledServices = []
      this.$emit('tenant-selected', this.selectedTenant)
    },
    onBuildingChange() {
      if (this.selectedBuilding) {
        this.loadEnabledServices()
      } else {
        this.enabledServices = []
      }
    },
    loadServiceList() {
      getAdminServiceList(
        (res) => {
          this.serviceList = res.data || []
        },
        () => {
          this.serviceList = []
        }
      )
    },
    loadEnabledServices() {
      if (!this.selectedTenant || !this.selectedBuilding) return
      this.loading = true
      getAdminServices(
        this.selectedTenant,
        this.selectedBuilding,
        (res) => {
          const list = res.data || []
          this.enabledServices = list.map((s) => ({
            ...s,
            serviceName: this.getServiceName(s.ServiceId)
          }))
          this.loading = false
        },
        () => {
          this.loading = false
          this.showMessage('Failed to load services')
        }
      )
    },
    getServiceName(serviceId) {
      const found = this.serviceList.find((s) => s.serviceId === serviceId)
      return found ? found.serviceName : serviceId
    },
    addService() {
      if (!this.selectedServiceToAdd) return
      this.loading = true
      postAdminService(
        {
          tenantId: this.selectedTenant,
          buildingId: this.selectedBuilding,
          serviceId: this.selectedServiceToAdd
        },
        () => {
          this.loading = false
          this.selectedServiceToAdd = null
          this.showMessage('Service added')
          this.loadEnabledServices()
        },
        (err) => {
          this.loading = false
          this.showMessage(err?.response?.data?.error || 'Failed to add')
        }
      )
    },
    removeService(item) {
      if (!confirm('Remove this service from the building?')) return
      this.loading = true
      deleteAdminService(
        {
          tenantId: this.selectedTenant,
          buildingId: this.selectedBuilding,
          serviceId: item.ServiceId
        },
        () => {
          this.loading = false
          this.showMessage('Service removed')
          this.loadEnabledServices()
        },
        (err) => {
          this.loading = false
          this.showMessage(err?.response?.data?.error || 'Failed to remove')
        }
      )
    },
    syncServiceList() {
      this.loading = true
      postAdminSyncServiceList(
        (res) => {
          this.loading = false
          const data = res.data || {}
          this.showMessage(data.message || `Synced ${data.totalAdded || 0} service(s)`)
          this.loadServiceList()
        },
        (err) => {
          this.loading = false
          this.showMessage(err?.response?.data?.error || 'Sync failed')
        }
      )
    },
    showMessage(msg) {
      this.snackbarMessage = msg
      this.snackbar = true
    }
  }
}
</script>
